import { DealType } from 'components/common/types/Deal.types';
import { OPEN_STREET_MAP_DATA_PROVIDER } from 'components/pages/Planner/PlannerSections/DataTargetingContent/ProgrammaticPointsOfInterest/OpenStreetMapPoiSearch/consts';
import { FormatLabel } from 'store/userOptions/reducer.types';

export const getSweepVisibility = (dealTypeCode, hasNonGuaranteedExtendedTargetEnabled) =>
  dealTypeCode === DealType.GUARANTEED || hasNonGuaranteedExtendedTargetEnabled;

export const getPoiPoints = (poiPoints) => {
  if (!poiPoints) return [];

  return poiPoints
    .filter(({ properties }) => !!properties)
    .map(({ geometry, id, include, name, properties, radius }) => ({
      geometry,
      id,
      include,
      name,
      properties: properties.map(
        ({ address, categoryName, categoryType, dataProvider, latitude, longtitude, mediaOwner, postalCode }) => ({
          address: address || '',
          categoryName,
          categoryType,
          dataProvider,
          latitude,
          longtitude,
          mediaOwner,
          poiId: id,
          poiName: name,
          postalCode,
          visibility: {
            include,
            radius,
          },
        }),
      )[0],
      radius,
    }));
};

export const getPlannerPoi = (plannerPoi) => {
  return plannerPoi.map(({ categoryName, categoryType, include, poiPoints, radius, dataProvider }) => {
    const categories = categoryType?.split('/').map((c) => c.trim()) ?? [];
    const categoryPath = `${categoryType}/${categoryName}`;

    return {
      categoryName,
      categoryType,
      code: categoryType,
      name: categoryName,
      poiPoints: getPoiPoints(poiPoints),
      include,
      radius,
      tooltip: `${categoryType} > ${categoryName}`,
      categoryPath,
      hasChildren: false,
      dataProvider: dataProvider ?? '',
      rootCategoryType: categories[0] ?? '',
      parentCategory: categories[categories.length - 1] ?? '',
    };
  });
};

export const getOpenStreetMapPoi = (openStreetMapPoi) => {
  return openStreetMapPoi.map(({ name, include, poiPoints, radius }) => ({
    name,
    code: name,
    dataProvider: OPEN_STREET_MAP_DATA_PROVIDER.value,
    hasChildren: false,
    poiPoints: getPoiPoints(poiPoints),
    include,
    radius,
    categoryType: name,
    categoryPath: name,
    rootCategoryType: name,
    parentCategory: '',
    tooltip: name,
  }));
};

export const getProximity = (proximity) => ({
  proximity: {
    plannerPoi: proximity?.plannerPoi ? getPlannerPoi(proximity.plannerPoi) : [],
    openStreetMapPoi: proximity?.openStreetMapPoi ? getOpenStreetMapPoi(proximity.openStreetMapPoi) : [],
  },
});

export const getTags = (tags) => {
  if (!tags) return {};
  return { tags: tags.map(({ tagCode, tagName, include }) => ({ code: tagCode, name: tagName, include })) };
};

export const getLocationData = (countries, cities, streets, counties, postCodes) => ({
  ...(countries ? { countries } : {}),
  ...(cities ? { cities } : {}),
  ...(streets ? { streets } : {}),
  ...(counties ? { counties } : {}),
  ...(postCodes ? { postCodes } : {}),
});

export const getListFrames = (includeFrames = [], excludeFrames = []) => {
  const routeFrameCodes = [];
  const listFiles = [];

  const addRouteFrameCodes = (frames, include) => {
    frames?.forEach((code) => {
      routeFrameCodes.push({
        code: code.assetId || code,
        ...(code.mediaOwner ? { mediaOwner: code.mediaOwner } : {}),
        name: code.assetId,
        include,
      });
    });
  };

  const addListFiles = (fileId, name, include, dynamoDbFileId, frames) => {
    listFiles.push({
      fileId,
      name,
      include,
      dynamoDbFileId,
      fileContents: frames?.map(({ assetId, mediaOwner }) => ({
        code: assetId,
        name: assetId,
        mediaOwner,
        include,
      })),
    });
  };

  includeFrames?.forEach(({ frames = [], fileId, fileName, dynamoDbFileId, fileFrames = [] }) => {
    if (fileName) {
      addListFiles(fileId, fileName, true, dynamoDbFileId, fileFrames);
    } else {
      addRouteFrameCodes(frames, true);
    }
  });

  excludeFrames?.forEach(({ frames = [], fileId, fileName, dynamoDbFileId, fileFrames = [] }) => {
    if (fileName) {
      addListFiles(fileId, fileName, false, dynamoDbFileId, fileFrames);
    } else {
      addRouteFrameCodes(frames, false);
    }
  });

  return { routeFrameCodes, listFiles };
};

export const getVisualUnitFrames = (includeVisualUnits = [], excludeVisualUnits = []) => {
  const visualUnitCodes = [];
  const visualUnitFiles = [];

  const addRouteFrameCodes = (frames, include) => {
    frames?.forEach((code) => {
      visualUnitCodes.push({
        code: code.assetId || code,
        ...(code.mediaOwner ? { mediaOwner: code.mediaOwner } : {}),
        name: code.assetId || code,
        include,
      });
    });
  };

  const addListFiles = (fileId, name, include, dynamoDbFileId, frames) => {
    if (!name) return;
    visualUnitFiles.push({
      fileId,
      name,
      include,
      dynamoDbFileId,
      fileContents: frames?.map(({ assetId, mediaOwner }) => {
        return {
          code: assetId,
          name: assetId,
          mediaOwner,
          include,
        };
      }),
    });
  };

  includeVisualUnits?.forEach(({ frames = [], fileId, fileName, dynamoDbFileId, fileFrames = [] }) => {
    addRouteFrameCodes(frames, true);
    addListFiles(fileId, fileName, true, dynamoDbFileId, fileFrames);
  });

  excludeVisualUnits?.forEach(({ frames = [], fileId, fileName, dynamoDbFileId, fileFrames = [] }) => {
    addRouteFrameCodes(frames, false);
    addListFiles(fileId, fileName, false, dynamoDbFileId, fileFrames);
  });

  return { visualUnitCodes, visualUnitFiles };
};

export const getVenueTaxonomies = (venueTaxonomies) => (venueTaxonomies ? { venueTaxonomies } : {});

export const getProductFormats = (productFormats) => {
  return productFormats.map(({ code, name }) => ({
    code,
    name,
    category: FormatLabel.productFormat,
    include: true,
  }));
};
